import type { PageResult } from '@jiaofa/api-types'
import { request } from './request'

export interface ShareSummary {
  enabled: boolean
  pendingCount: number
  sharingCount: number
  sharedCount: number
  closedCount: number
  manualCount: number
  sharedAmountFen: number
  returnPendingCount: number
  returnFailedCount: number
}

export interface SharePayment {
  id: number
  paymentId: number
  orderId: number
  orderNo?: string
  storeId?: number
  paidAmount?: number
  ruleVersion?: string
  status: string
  updatedAt?: string
}

export interface ShareOrderDetail {
  id: number
  receiverAccount?: string
  receiverName?: string
  amount?: number
  result?: string
  failReason?: string
}

export interface ShareOrder {
  id: number
  paymentId: number
  orderId: number
  orderNo?: string
  outOrderNo?: string
  kind: 'SHARE' | 'UNFREEZE' | string
  amount?: number
  ruleVersion?: string
  state: string
  failReason?: string
  updatedAt?: string
  details?: ShareOrderDetail[]
}

export interface ShareReturn {
  id: number
  outReturnNo?: string
  wechatReturnId?: string
  amount?: number
  result: string
  failReason?: string
  retryCount?: number
  updatedAt?: string
}

export interface ShareRuleItem {
  id?: number
  receiverId: number
  receiverAccount?: string
  receiverName?: string
  receiverStatus?: string
  baseType?: 'PAID_AMOUNT' | 'GOODS_AMOUNT' | string
  ratioBp?: number
  fixedFen?: number
  minFen?: number
  maxFen?: number
  sort?: number
}

export interface ShareRule {
  id: number
  name: string
  storeId?: number
  storeName?: string
  global?: boolean
  version: number
  enabled?: boolean
  ruleVersion?: string
  effectiveFrom?: string
  remark?: string
  items: ShareRuleItem[]
}

export interface ShareReceiver {
  id: number
  storeId?: number
  account: string
  name?: string
  relationType?: string
  customRelation?: string
  status: string
  returnAllowed?: boolean
  updatedAt?: string
}

export interface ShareReceiverSavePayload {
  storeId?: number
  account: string
  name: string
  relationType: string
  customRelation?: string
  returnAllowed?: boolean
}

export interface ShareRuleSavePayload {
  storeId?: number
  name: string
  remark?: string
  items: Array<{
    receiverId: number
    baseType?: 'PAID_AMOUNT' | 'GOODS_AMOUNT'
    ratioBp?: number
    fixedFen?: number
    minFen?: number
    maxFen?: number
    sort?: number
  }>
}

export const fetchShareSummary = async (): Promise<ShareSummary> =>
  request<ShareSummary>({ url: '/profit-sharing/summary', method: 'GET' })

export const fetchSharePayments = async (params: { status?: string; storeId?: number; page?: number; pageSize?: number }): Promise<PageResult<SharePayment>> =>
  request<PageResult<SharePayment>>({ url: '/profit-sharing/payments', method: 'GET', params })

export const executeSharePayment = async (id: number): Promise<SharePayment> =>
  request<SharePayment>({ url: `/profit-sharing/payments/${id}/execute`, method: 'POST' })

export const unfreezeSharePayment = async (id: number): Promise<SharePayment> =>
  request<SharePayment>({ url: `/profit-sharing/payments/${id}/unfreeze`, method: 'POST' })

export const fetchShareOrders = async (params: { state?: string; kind?: string; page?: number; pageSize?: number }): Promise<PageResult<ShareOrder>> =>
  request<PageResult<ShareOrder>>({ url: '/profit-sharing/orders', method: 'GET', params })

export const fetchShareOrderDetail = async (id: number): Promise<ShareOrder> =>
  request<ShareOrder>({ url: `/profit-sharing/orders/${id}`, method: 'GET' })

export const requeryShareOrder = async (id: number): Promise<ShareOrder> =>
  request<ShareOrder>({ url: `/profit-sharing/orders/${id}/requery`, method: 'POST' })

export const fetchShareReturns = async (params: { result?: string; page?: number; pageSize?: number }): Promise<PageResult<ShareReturn>> =>
  request<PageResult<ShareReturn>>({ url: '/profit-sharing/returns', method: 'GET', params })

export const requeryShareReturn = async (id: number): Promise<ShareReturn> =>
  request<ShareReturn>({ url: `/profit-sharing/returns/${id}/requery`, method: 'POST' })

export const fetchShareRules = async (storeId?: number): Promise<ShareRule[]> =>
  request<ShareRule[]>({ url: '/profit-sharing/rules', method: 'GET', params: storeId ? { storeId } : undefined })

export const saveShareRule = async (payload: ShareRuleSavePayload): Promise<ShareRule> =>
  request<ShareRule>({ url: '/profit-sharing/rules', method: 'POST', data: payload })

export const disableShareRule = async (id: number): Promise<ShareRule> =>
  request<ShareRule>({ url: `/profit-sharing/rules/${id}/disable`, method: 'POST' })

export const fetchShareReceivers = async (): Promise<ShareReceiver[]> =>
  request<ShareReceiver[]>({ url: '/profit-sharing/receivers', method: 'GET' })

export const createShareReceiver = async (payload: ShareReceiverSavePayload): Promise<ShareReceiver> =>
  request<ShareReceiver>({ url: '/profit-sharing/receivers', method: 'POST', data: payload })

export const activateShareReceiver = async (id: number): Promise<ShareReceiver> =>
  request<ShareReceiver>({ url: `/profit-sharing/receivers/${id}/activate`, method: 'POST' })

export const removeShareReceiver = async (id: number): Promise<ShareReceiver> =>
  request<ShareReceiver>({ url: `/profit-sharing/receivers/${id}/remove`, method: 'POST' })
